/**
 * Rotating "did you know" / example-prompt suggestions shown on Home (a
 * longer, educational tip below the hero subtitle) and in Chat (a short,
 * directly-typeable example, e.g. as a clickable chip). Content and the
 * category split (Home = educational tour of everything Flow can do, Chat =
 * short brain/memory + per-category prompts) came directly from product
 * discussion — see PR/commit description for the full back-and-forth.
 *
 * Language suggestions are deliberately UNDER-weighted relative to their
 * raw count (they're a large chunk of both lists) — shown roughly 1 in 6
 * picks, sometimes standalone and sometimes appended as a short flourish
 * onto an otherwise-normal pick, rather than competing equally with every
 * other category on raw list size.
 */

// ---- Home: educational, longer-form tips ----

const HOME_BRAIN_TIPS = [
  "Hi, I have a brain — I can remember a lot about you, across every project, not just this chat.",
  "I have a memory of my own. Tell me a preference once and I'll carry it everywhere.",
  "Hi, I'm not just a chatbot — I have a brain that remembers you between sessions.",
  'Did you know you can save tokens? Ask me to save a small program in your brain for a problem you solve often, instead of asking an LLM to redo it every time.',
];

const HOME_CODING_TIPS = [
  'Did you know I can write and edit real, running code — not just suggest snippets?',
  'Ask me to build a full app from scratch. No code needed on your side.',
  'I can read your codebase and make changes across multiple files at once.',
];

const HOME_BRAINSTORM_TIPS = [
  "Not sure where to start? Describe your idea and I'll help shape it into a plan.",
  'Stuck on a decision? Talk it through with me before writing a single line of code.',
  'I can turn a rough idea into a structured business plan, pitch, or roadmap.',
];

const HOME_LEGACY_TIPS = [
  "I can review an old codebase and tell you what's risky, outdated, or worth keeping.",
  "Point me at a legacy project and I'll map out what it actually does.",
  'Did you know I can assess technical debt and suggest a modernization plan?',
];

const HOME_GIT_CLONE_TIPS = [
  "Give me a list of repositories and I'll clone and set them all up together.",
  'I can pull in multiple git repos and wire them into one working project.',
  'Did you know I can bootstrap a whole multi-service project from existing repos?',
];

const HOME_INTERNAL_TOOLS_TIPS = [
  "Need a quick internal tool? Describe what your team needs and I'll build it.",
  'I can put together dashboards, admin panels, or scripts for internal use.',
  'Did you know I can build internal tools your team can start using today?',
];

const HOME_SCRIPTS_TIPS = [
  'I can write a script to automate that repetitive task for you.',
  'Need a one-off script? Just describe what it should do.',
  'Did you know I can write and run scripts directly, not just show you code?',
];

const HOME_GENERAL_TIPS = [
  "Ask me anything — plan, build, fix, brainstorm, or automate. I'll figure out the right approach.",
  'From a landing page to a full backend, I can build it end-to-end.',
  'I can fix bugs, refactor messy code, or start something brand new — your call.',
];

const HOME_LANGUAGE_TIPS = [
  'I speak italiano, ciao!',
  'I speak français, bonjour!',
  'I speak español, ¡hola!',
  'I speak Deutsch, hallo!',
  'I speak português, olá!',
  'I speak 中文, 你好!',
  'I speak 日本語, こんにちは!',
  'I speak 한국어, 안녕하세요!',
  'I speak العربية, مرحبا!',
  'I speak हिन्दी, नमस्ते!',
  'I speak русский, привет!',
  'I speak Nederlands, hallo!',
  'I speak polski, cześć!',
  'I speak Türkçe, merhaba!',
  'I speak Tiếng Việt, xin chào!',
  'I speak ไทย, สวัสดี!',
  'I speak svenska, hej!',
  'I speak ελληνικά, γεια σου!',
  'I speak עברית, שלום!',
  'I speak Bahasa Indonesia, halo!',
  "Talk to me in whatever language is easiest for you — I'll follow.",
];

const HOME_GENERAL_POOL = [
  ...HOME_BRAIN_TIPS,
  ...HOME_CODING_TIPS,
  ...HOME_BRAINSTORM_TIPS,
  ...HOME_LEGACY_TIPS,
  ...HOME_GIT_CLONE_TIPS,
  ...HOME_INTERNAL_TOOLS_TIPS,
  ...HOME_SCRIPTS_TIPS,
  ...HOME_GENERAL_TIPS,
];

// ---- Chat: short, directly-typeable example prompts ----

const CHAT_BRAIN_PROMPTS = [
  'Remember that my favorite color is yellow',
  'Remember I always want TypeScript, not JavaScript',
  'Search my bio online and save what you find',
  "Check how I work and remember it — I don't want to repeat myself",
  'Digital twin me',
  'Learn my style from this conversation and keep it',
  'Remember I run a mindfulness studio for beginners',
  'Save my brand colors so you use them next time',
  'Remember I like short, casual copy — no corporate tone',
  "Don't ask me twice — remember my answer",
  'Build my digital twin from what I tell you',
];

const CHAT_CODING_PROMPTS = [
  'Build me a landing page for my business',
  'Add a login page with email and password',
  'Fix the bug in my checkout flow',
  'Refactor this file to be cleaner',
];

const CHAT_BRAINSTORM_PROMPTS = [
  'Help me plan my business idea',
  'Give me three names for my startup',
  'Turn this idea into a pitch deck',
];

const CHAT_LEGACY_PROMPTS = [
  'Review my old codebase and tell me what\'s risky',
  "Assess this project's technical debt",
  "What's outdated in my repo?",
];

const CHAT_GIT_CLONE_PROMPTS = [
  'Clone these repos and set them up together',
  'Pull in my other project and connect it to this one',
];

const CHAT_INTERNAL_TOOLS_PROMPTS = [
  'Build me an internal dashboard',
  'Make a simple admin panel for my team',
];

const CHAT_SCRIPTS_PROMPTS = [
  'Write a script to automate this task',
  'Give me a script to clean up my CSV files',
];

const CHAT_LANGUAGE_PROMPTS = [
  'Parlami in italiano',
  'Parle-moi en français',
  'Háblame en español',
  'Sprich mit mir auf Deutsch',
  '用中文跟我说话',
  '日本語で話しかけて',
  '한국어로 말해줘',
  'تحدث معي بالعربية',
  'मुझसे हिंदी में बात करो',
  'Говори со мной по-русски',
];

const CHAT_GENERAL_POOL = [
  ...CHAT_BRAIN_PROMPTS,
  ...CHAT_CODING_PROMPTS,
  ...CHAT_BRAINSTORM_PROMPTS,
  ...CHAT_LEGACY_PROMPTS,
  ...CHAT_GIT_CLONE_PROMPTS,
  ...CHAT_INTERNAL_TOOLS_PROMPTS,
  ...CHAT_SCRIPTS_PROMPTS,
];

// Language picks land roughly 1 in LANGUAGE_FREQUENCY tries (middle of the
// requested "every 5-8 other suggestions" range) — and even then, only
// HALF the time as a standalone language line; the other half, a short
// language flourish is appended onto a normal (non-language) pick instead.
const LANGUAGE_FREQUENCY = 6;

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

/**
 * @param {'home'|'chat'} surface
 * @returns {string}
 */
export function getRandomSuggestion(surface) {
  const generalPool = surface === 'chat' ? CHAT_GENERAL_POOL : HOME_GENERAL_POOL;
  const languagePool = surface === 'chat' ? CHAT_LANGUAGE_PROMPTS : HOME_LANGUAGE_TIPS;

  const isLanguageTurn = Math.floor(Math.random() * LANGUAGE_FREQUENCY) === 0;

  if (!isLanguageTurn) {
    return pickRandom(generalPool);
  }

  // Chat's suggestion is a clickable chip that sends its own text VERBATIM
  // on click (ChatWidgetOverview.prompts — "Clicking one sends it") —
  // appending a language flourish there would garble the actual message
  // sent (live-confirmed: "Build my digital twin from what I tell you
  // (Parle-moi en français)" sent as one message the user would have had
  // to edit first). Home's tip is plain display text with no send action,
  // so the flourish is harmless there.
  if (surface === 'chat') {
    return pickRandom(languagePool);
  }

  const appendFlourish = Math.random() < 0.5;
  if (!appendFlourish) {
    return pickRandom(languagePool);
  }

  const base = pickRandom(generalPool);
  const flourish = pickRandom(languagePool);
  return `${base} (${flourish})`;
}

/**
 * `count` distinct suggestions (retries on a duplicate rather than allowing
 * one, up to a small attempt cap so a small pool can't loop forever) — used
 * for Chat's multiple suggestion chips.
 * @param {'home'|'chat'} surface
 * @param {number} count
 * @returns {string[]}
 */
export function getRandomSuggestions(surface, count) {
  const result = [];
  const seen = new Set();
  const maxAttempts = count * 20;
  let attempts = 0;
  while (result.length < count && attempts < maxAttempts) {
    attempts += 1;
    const next = getRandomSuggestion(surface);
    if (seen.has(next)) continue;
    seen.add(next);
    result.push(next);
  }
  return result;
}

export default { getRandomSuggestion, getRandomSuggestions };
